import React from 'react';
import clsx from 'clsx';

import { FILTER_TREE_TYPE, SEARCH_FILTER_NAME } from 'constants/filter';
import { TransformedFilterTreeNode } from 'components/tree/FilterTree';
import FilterSearchPanel, { FilterSearchPanelProps } from 'components/panel/filter/FilterSearchPanel';

//
// --- Types ---

export interface GlobalFilterSearchPanelProps
  extends Omit<
    FilterSearchPanelProps,
    'filterTreeData' | 'isFilterTreeDataLoading' | 'onFilterOpenStateChange' | 'openFilterIds'
  > {
  activeSearchFilterNames: ReadonlyArray<string>;
  disabledSearchFilterNames: ReadonlyArray<string>;
}

//
// --- Constants ---

const baseGlobalTreNode: Partial<TransformedFilterTreeNode> = {
  children: [],
  filterTreeType: null,
  id: '',
  name: null,
  parentId: null
};

const defaultParentGlobalTreeNode: TransformedFilterTreeNode = {
  ...(baseGlobalTreNode as TransformedFilterTreeNode),
  checked: null,
  leaf: false,
  open: true
};

const defaultChildGlobalTreeNode: TransformedFilterTreeNode = {
  ...(baseGlobalTreNode as TransformedFilterTreeNode),
  checked: false,
  leaf: true,
  open: false
};

//
// --- Default Tree Node ---

export const defaultGlobalFilterTreeNodes: ReadonlyArray<TransformedFilterTreeNode> = [
  {
    ...defaultParentGlobalTreeNode,
    children: [
      {
        ...defaultChildGlobalTreeNode,
        filterTreeType: FILTER_TREE_TYPE.CONTACT,
        id: SEARCH_FILTER_NAME.ACTIVE_USER,
        name: 'Active Only',
        parentId: 'global-filter-node-1',
        tooltip: 'View only Active Users who have logged in within the past year'
      },
      {
        ...defaultChildGlobalTreeNode,
        filterTreeType: FILTER_TREE_TYPE.CONTACT,
        id: SEARCH_FILTER_NAME.POWERSCORE,
        name: 'Contacts with PowerScore',
        parentId: 'global-filter-node-1',
        tooltip:
          'View only users who have participated in at least one Bid Event, Portal, or Contract with your company'
      }
    ],
    filterTreeType: FILTER_TREE_TYPE.CONTACT,
    id: 'global-filter-node-1',
    name: 'Contact Filters'
  }
];

//
// --- GlobalFilterSearchPanel Component ---

const GlobalFilterSearchPanel: React.FC<GlobalFilterSearchPanelProps> = props => {
  const {
    className,
    activeSearchFilterNames = [],
    disabledSearchFilterNames = [],
    ...remainingFilterSearchPanelProps
  } = props;

  const [openTreeNodeIds, setOpenTreeNodeIds] = React.useState<Array<string>>([]);

  const handleFilterOpenStateChange: FilterSearchPanelProps['onFilterOpenStateChange'] = (openFilterId, isOpen) => {
    setOpenTreeNodeIds(isOpen ? [...openTreeNodeIds, openFilterId] : openTreeNodeIds.filter(id => id !== openFilterId));
  };

  const treeData = defaultGlobalFilterTreeNodes.map(treeNode => ({
    ...treeNode,
    open: Array.isArray(openTreeNodeIds) ? openTreeNodeIds.includes(treeNode.id) : false,
    children: Array.isArray(treeNode.children)
      ? treeNode.children
          .filter(childNode => !disabledSearchFilterNames.includes(childNode.id))
          .map(childNode => ({ ...childNode, checked: activeSearchFilterNames.includes(childNode.id) }))
      : []
  }));

  return (
    <FilterSearchPanel
      className={clsx('global-filter-search-panel', className)}
      data-testid="global-filter-search-panel"
      filterTreeData={treeData}
      onFilterOpenStateChange={handleFilterOpenStateChange}
      openFilterIds={openTreeNodeIds}
      {...remainingFilterSearchPanelProps}
    />
  );
};

export default GlobalFilterSearchPanel;
